import { planDataFor, uniquePlanName, type PlanFolderRow, type PlanRow } from '@coach/core';
import { Empty, Modal, useConfirm, useToast } from '@coach/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../lib/app';
import { usePlanStore } from '../plan/liveSession';

const ALL = '__all__';
const NONE = '__none__';

/**
 * Saved plans — folders, search, trash. Lives inside the Plan page now
 * (📁 Plans opens it as a sheet); loading a plan closes the sheet.
 */
export function SavedPlans({ onLoaded }: { onLoaded: () => void }) {
  const app = useApp();
  const store = usePlanStore();
  const toast = useToast();
  const confirm = useConfirm();
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [folders, setFolders] = useState<PlanFolderRow[]>([]);
  const [trash, setTrash] = useState<PlanRow[]>([]);
  const [folder, setFolder] = useState<string>(ALL);
  const [folderModal, setFolderModal] = useState<{ mode: 'new' | 'rename'; name: string } | null>(null);
  const [moving, setMoving] = useState<PlanRow | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [q, setQ] = useState('');

  const reload = useCallback(async () => {
    const [p, f, t] = await Promise.all([app.repos.plans.listLive(), app.repos.folders.listLive(), app.repos.plans.listTrash()]);
    setPlans(p.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)));
    setFolders(f.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)));
    setTrash(t.sort((a, b) => (a.deleted_at! < b.deleted_at! ? 1 : -1)));
  }, [app.repos]);
  useEffect(() => {
    void reload();
  }, [reload, app.syncStatus.lastSyncAt]);

  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return plans.filter((p) => (folder === ALL ? true : folder === NONE ? !p.folder_id : p.folder_id === folder)).filter((p) => !qq || p.name.toLowerCase().includes(qq));
  }, [plans, folder, q]);

  const canEdit = (p: PlanRow) => app.isHead || p.owner_id === app.userId;
  const curFolder = folders.find((f) => f.id === folder);

  const isShell = (p: PlanRow) => p.data?.shell === true;
  const shells = visible.filter(isShell);
  const dated = visible.filter((p) => !isShell(p));

  /** Opening a plan. A shell starts a dated copy; everything else opens as-is. */
  const load = (p: PlanRow) => {
    if (isShell(p)) {
      void store.startFromShell(p).then((c) => { if (c) onLoaded(); });
      return;
    }
    store.loadRow(p);
    onLoaded();
  };
  /** The deliberate way into a shell itself. */
  const editShell = (p: PlanRow) => {
    store.loadRow(p);
    onLoaded();
  };
  const setShell = async (p: PlanRow, on: boolean) => {
    const cur = store.plan._id === p.id;
    if (cur) {
      store.setPlan((c) => ({ ...c, shell: on ? true : undefined })); // autosave writes it
    } else {
      await app.repos.plans.save({ ...p, data: planDataFor({ ...p.data, shell: on }) });
    }
    toast(on ? `🧩 “${p.name}” is a shell now — opening it starts a dated copy` : `“${p.name}” is a regular plan again`);
    setTimeout(() => void reload(), cur ? 1500 : 0);
  };
  const copy = async (p: PlanRow) => {
    const name = uniqueName(p.name + ' (copy)', plans);
    await app.repos.plans.save(app.repos.plans.blank({ name, folder_id: p.folder_id, data: planDataFor({ ...p.data, shell: false }, name) }));
    toast('Copied');
    void reload();
  };
  const del = async (p: PlanRow) => {
    if (!(await confirm(`Move "${p.name}" to the trash?`, { ok: 'Trash', danger: true }))) return;
    // Drop it as the working plan first, so a pending autosave can't save it back.
    if (store.plan._id === p.id) store.discardCurrent();
    await app.repos.plans.trash(p.id);
    toast('Moved to trash');
    void reload();
  };
  const restore = async (p: PlanRow) => {
    const clash = plans.find((x) => x.name.toLowerCase() === p.name.toLowerCase());
    if (clash) {
      const nn = uniqueName(p.name, plans);
      await app.repos.plans.save({ ...p, name: nn, data: { ...p.data, name: nn } });
    }
    await app.repos.plans.restore(p.id);
    toast('Restored');
    void reload();
  };
  const purge = async (p: PlanRow) => {
    if (!(await confirm(`Permanently delete "${p.name}"? This cannot be undone.`, { ok: 'Delete forever', danger: true }))) return;
    await app.db.plans.delete(p.id);
    if (app.mode === 'cloud') {
      const { supabase } = await import('../../lib/supabase');
      await supabase!.rpc('purge_row', { tbl: 'plans', row_id: p.id });
    }
    toast('Deleted');
    void reload();
  };
  const saveFolder = async () => {
    if (!folderModal) return;
    const name = folderModal.name.trim();
    if (!name) return;
    if (folderModal.mode === 'new') {
      const f = await app.repos.folders.save(app.repos.folders.blank({ name, sort_order: folders.length }));
      setFolder(f.id);
    } else if (curFolder) await app.repos.folders.save({ ...curFolder, name });
    setFolderModal(null);
    void reload();
  };
  const deleteFolder = async () => {
    if (!curFolder) return;
    if (!(await confirm(`Delete folder "${curFolder.name}"? Plans inside are kept and become unfiled.`, { ok: 'Delete folder', danger: true }))) return;
    for (const p of plans.filter((x) => x.folder_id === curFolder.id)) await app.repos.plans.save({ ...p, folder_id: null });
    await app.repos.folders.trash(curFolder.id);
    setFolder(ALL);
    void reload();
  };
  const moveTo = async (fid: string | null) => {
    if (!moving) return;
    await app.repos.plans.save({ ...moving, folder_id: fid });
    setMoving(null);
    void reload();
  };

  const renderRow = (p: PlanRow) => {
    const isPosted = store.posted.info?.plan_id === p.id;
    const shell = isShell(p);
    return (
      <div key={p.id} className={'list-item' + (shell ? ' shell-row' : '')}>
        <div className="grow" style={{ cursor: 'pointer' }} onClick={() => load(p)}>
          <div>
            {shell && '🧩 '}{p.name} {isPosted && <span className="chip on">📌 Today</span>}
            {!canEdit(p) && <span className="chip">🔒</span>}
          </div>
          <div className="muted small">
            {p.data?.periods?.length ?? 0} periods · {p.data?.startTime || ''} · {new Date(p.updated_at).toLocaleDateString()}
            {p.folder_id && folder === ALL ? ` · ${folders.find((f) => f.id === p.folder_id)?.name ?? ''}` : ''}
          </div>
        </div>
        {/* Opening is the common case, so it gets its own button beside ⋯. */}
        <button className="btn btn-sm btn-primary" onClick={() => load(p)} title={shell ? 'Start a practice from this shell' : 'Open this plan'}>{shell ? 'Start' : 'Open'}</button>
        <PlanMenu
          canEdit={canEdit(p)}
          isHead={app.isHead}
          isPosted={isPosted}
          isShell={shell}
          onOpen={() => load(p)}
          onEditShell={() => editShell(p)}
          onToggleShell={() => void setShell(p, !shell)}
          onCopy={() => void copy(p)}
          onMove={() => setMoving(p)}
          onTrash={() => void del(p)}
          onPost={() => (isPosted ? void store.unpost() : void store.postPlanId(p.id, p.name))}
        />
      </div>
    );
  };

  return (
    <div>
      <div className="folder-tabs" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 0 0' }}>
        <button className={'chip chip-btn' + (folder === ALL ? ' on' : '')} onClick={() => setFolder(ALL)}>All</button>
        <button className={'chip chip-btn' + (folder === NONE ? ' on' : '')} onClick={() => setFolder(NONE)}>Unfiled</button>
        {folders.map((f) => (
          <button key={f.id} className={'chip chip-btn' + (folder === f.id ? ' on' : '')} onClick={() => setFolder(f.id)}>📁 {f.name}</button>
        ))}
        {app.isHead && <button className="chip chip-btn" onClick={() => setFolderModal({ mode: 'new', name: '' })}>＋ Folder</button>}
      </div>
      <div className="row" style={{ padding: '10px 0 0' }}>
        <input placeholder="Search plans" value={q} onChange={(e) => setQ(e.target.value)} />
        {app.isHead && curFolder && (
          <>
            <button className="btn btn-sm" onClick={() => setFolderModal({ mode: 'rename', name: curFolder.name })}>Rename</button>
            <button className="btn btn-sm btn-danger" onClick={() => void deleteFolder()}>Delete</button>
          </>
        )}
        <button className="btn btn-sm" onClick={() => setTrashOpen(true)}>🗑 {trash.length}</button>
      </div>
      {visible.length === 0 ? (
        <Empty>No saved plans{q ? ' match' : ''} here yet.</Empty>
      ) : (
        <>
          {shells.length > 0 && (
            <>
              <div className="shell-section">🧩 Shells — tap to start a practice from one</div>
              <div className="list" style={{ margin: 0 }}>{shells.map((p) => renderRow(p))}</div>
              {dated.length > 0 && <div className="shell-section">Plans</div>}
            </>
          )}
          {dated.length > 0 && <div className="list" style={{ margin: shells.length ? 0 : '10px 0 0' }}>{dated.map((p) => renderRow(p))}</div>}
        </>
      )}

      <Modal open={!!folderModal} onClose={() => setFolderModal(null)} title={folderModal?.mode === 'new' ? 'New folder' : 'Rename folder'} actions={<><button className="btn" onClick={() => setFolderModal(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void saveFolder()}>Save</button></>}>
        <input autoFocus value={folderModal?.name ?? ''} onChange={(e) => setFolderModal((m) => (m ? { ...m, name: e.target.value } : m))} onKeyDown={(e) => e.key === 'Enter' && void saveFolder()} placeholder="Folder name" />
      </Modal>

      <Modal open={!!moving} onClose={() => setMoving(null)} title={`Move "${moving?.name ?? ''}"`}>
        <div className="list" style={{ margin: 0 }}>
          <button className="list-item" style={{ width: '100%', border: 'none', cursor: 'pointer' }} onClick={() => void moveTo(null)}>Unfiled</button>
          {folders.map((f) => (
            <button key={f.id} className="list-item" style={{ width: '100%', border: 'none', cursor: 'pointer' }} onClick={() => void moveTo(f.id)}>📁 {f.name}</button>
          ))}
        </div>
      </Modal>

      <Modal open={trashOpen} onClose={() => setTrashOpen(false)} title="Trash">
        {trash.length === 0 ? <Empty>Trash is empty.</Empty> : (
          <div className="list" style={{ margin: 0 }}>
            {trash.map((p) => (
              <div key={p.id} className="list-item">
                <div className="grow">
                  <div>{p.name}</div>
                  <div className="muted small">deleted {new Date(p.deleted_at!).toLocaleString()}</div>
                </div>
                <button className="btn btn-sm btn-green" onClick={() => void restore(p)}>Restore</button>
                {app.isHead && <button className="btn btn-sm btn-danger" onClick={() => void purge(p)}>Purge</button>}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

function uniqueName(want: string, plans: PlanRow[]): string {
  return uniquePlanName(want, plans.map((p) => p.name));
}

function PlanMenu({ canEdit, isHead, isPosted, isShell, onOpen, onEditShell, onToggleShell, onCopy, onMove, onTrash, onPost }: { canEdit: boolean; isHead: boolean; isPosted: boolean; isShell: boolean; onOpen: () => void; onEditShell: () => void; onToggleShell: () => void; onCopy: () => void; onMove: () => void; onTrash: () => void; onPost: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)}>⋯</button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <div className="row row-wrap" style={{ gap: 8 }}>
          <button className="btn btn-primary" onClick={() => { setOpen(false); onOpen(); }}>{isShell ? 'Start a practice from it' : 'Open'}</button>
          {isShell && <button className="btn" onClick={() => { setOpen(false); onEditShell(); }}>{canEdit ? '✏️ Edit shell' : 'View shell'}</button>}
          <button className="btn" onClick={() => { setOpen(false); onCopy(); }}>Copy</button>
          {canEdit && <button className="btn" onClick={() => { setOpen(false); onToggleShell(); }}>{isShell ? 'Not a shell' : '🧩 Make it a shell'}</button>}
          {canEdit && <button className="btn" onClick={() => { setOpen(false); onMove(); }}>Move to folder</button>}
          {isHead && <button className="btn" onClick={() => { setOpen(false); onPost(); }}>{isPosted ? 'Take down Today’s Plan' : '📌 Post as Today’s Plan'}</button>}
          {canEdit && <button className="btn btn-danger" onClick={() => { setOpen(false); onTrash(); }}>Trash</button>}
        </div>
      </Modal>
    </>
  );
}
